import { useCallback, useContext } from 'react'
import { dispatchGtmEvent } from './providers/gtmAdapter'
import type { AnalyticsEvent, AnalyticsGlobalContext } from './types'
import { AnalyticsStateContext } from './context'

export type TrackEventFn = <E extends AnalyticsEvent>(event: E) => void

/**
 * Returns `trackEvent(event)`. Merges declared globals + extras + event.payload (per-event wins)
 * and dispatches to GTM. Merge rules: README.
 *
 * ```tsx
 * const trackEvent = useTrackEvent()
 * trackEvent({ type: 'DYNAMIC_CLICK_TRACKING', payload: { event_label: 'Save' } })
 * ```
 */
export function useTrackEvent(): TrackEventFn {
  const analyticsState = useContext(AnalyticsStateContext)

  return useCallback(<E extends AnalyticsEvent>(event: E) => {
    // Pick globals declared by this event's payload (typo-safe via AnalyticsPayload<…, TGlobalsConsumed>).
    type DeclaredGlobalKeys = Extract<keyof E['payload'], keyof AnalyticsGlobalContext>
    type DeclaredGlobals = Pick<E['payload'], DeclaredGlobalKeys>
    const declaredGlobals = analyticsState.globalContext as DeclaredGlobals

    const mergedEvent = {
      ...event,
      payload: { ...analyticsState.extras, ...declaredGlobals, ...event.payload },
    } as E

    dispatchGtmEvent<E>(mergedEvent)
  }, [analyticsState])
}
